import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, MailIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
type Variant = 'primary' | 'secondary';
type Size = 'md' | 'sm';
interface ActionLinkProps {
  to: string;
  children: React.ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
  icon?: React.ReactNode;
  showArrow?: boolean;
}
const variants: Record<Variant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-hover active:bg-brand-press',
  secondary: 'border border-ink bg-white text-ink hover:bg-mist active:bg-line'
};
const sizes: Record<Size, string> = {
  md: 'min-h-[48px] px-6 py-2.5 type-button',
  sm: 'min-h-[44px] px-5 py-2 type-button'
};

// Shared CTA shape: 15px corners, matching the logo mark.
export const ctaShape = 'rounded-[15px]';
export function ActionLink({
  to,
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  icon,
  showArrow
}: ActionLinkProps) {
  const leading = icon ?? contactIcon(to);
  const arrow = showArrow ?? !leading;
  const classes = `group inline-flex max-w-full items-center justify-center gap-2 text-center leading-tight sm:whitespace-nowrap ${ctaShape} font-bold transition-colors duration-150 ease-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${variants[variant]} ${sizes[size]} ${className}`;
  const content = <>
      {leading}
      <span data-id="element-736" data-mp="2v5chdwpi034">{children}</span>
      {arrow ? <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-brand group-hover:translate-x-0.5" aria-hidden="true" data-id="element-737" data-mp="3u8i5x7gm9ebv" /> : null}
    </>;
  if (isNativeHref(to)) {
    const external = to.startsWith('http');
    return <a href={to} className={classes} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} data-id="element-738" data-mp="3l9eoe7x3l6zh">
        {content}
      </a>;
  }
  return <Link to={to} className={classes} data-id="element-739" data-mp="3bafdsjsrv19u">
      {content}
    </Link>;
}
function contactIcon(to: string) {
  const cls = 'h-5 w-5 shrink-0';
  if (to.startsWith('mailto:')) return <MailIcon className={cls} aria-hidden="true" data-id="element-740" data-mp="36lealcbry3nq" />;
  if (to.startsWith('tel:')) return <PhoneIcon className={cls} aria-hidden="true" data-id="element-741" data-mp="2sl3ladq44x4o" />;
  if (to.includes('wa.me')) return <MessageCircleIcon className={cls} aria-hidden="true" data-id="element-742" data-mp="2sng8p6en3zp4" />;
  return null;
}
export function isNativeHref(to: string) {
  return to.startsWith('#') || to.startsWith('http') || to.startsWith('mailto:') || to.startsWith('tel:');
}